import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest, getApiOrigin } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import '../quotes/quote-generator.css';
import './settings.css';
import { useWorkspaceBranding } from '../../../branding/BrandingContext';
import NotificationsSettingsTab from './NotificationsSettingsTab';
import SecuritySettingsTab from './SecuritySettingsTab';
import TeamSettingsTab from './TeamSettingsTab';
import HoursApprovalSection from './HoursApprovalSection';
import PayrollSettingsSection, {
  type PayrollConnectorInfo,
  type PayrollSettingsState
} from './PayrollSettingsSection';
import { DEFAULT_PAYROLL_SETTINGS } from './PayrollConnectPicker';
import AdminFieldModeToggle from '../../../components/admin/AdminFieldModeToggle';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  type CredentialSettings,
  type NotificationSettingsState,
  type ProfileSettings,
  type SaveButtonState,
  type SessionInfo
} from './settingsTypes';

type Branding = {
  companyName?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  hstNumber?: string;
  brandColor?: string;
  logoBase64?: string;
  disclaimerText?: string;
  complianceText?: string;
  pdfTemplate?: 'generic' | 'premium' | 'zera';
  /** From GET/PUT response — derived from pdfTemplate + server policy. */
  pdfVariant?: 'premium' | 'generic';
  /** Server-derived: use shipped CFAA/ACMO marks when uploads are absent. */
  useBundledAssociationBadges?: boolean;
  /** Legacy raster-export flag — only present for the tenant licensed to use it. */
  useClientRasterExport?: boolean;
  cfaaLogoBase64?: string;
  acmoLogoBase64?: string;
  /** Custom PDF layout JSON — edited in /admin/pdf-studio. */
  pdfLayoutJson?: string;
};

type SettingsSection = 'general' | 'pdfManagement' | 'security' | 'team' | 'hoursApproval' | 'payroll' | 'notifications';

const defaultBranding: Branding = {
  companyName: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  hstNumber: '',
  brandColor: '#2e8a57',
  logoBase64: '',
  disclaimerText: '',
  complianceText: '',
  pdfTemplate: 'generic',
  pdfVariant: 'generic'
};

function validateNewPassword(password: string): string | null {
  if (password.length < 8) {
    return 'New password must be at least 8 characters.';
  }
  if (!/[A-Z]/.test(password)) {
    return 'New password must include at least one uppercase letter.';
  }
  if (!/[\d\W]/.test(password)) {
    return 'New password must include at least one number or special character.';
  }
  return null;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read logo file.'));
    reader.readAsDataURL(file);
  });
}

function isValidHex(value: string): boolean {
  return /^#([0-9a-fA-F]{6})$/.test(value.trim());
}

export default function SettingsPage(): React.JSX.Element {
  const { refreshBranding } = useWorkspaceBranding();
  const { refreshSession } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState<Branding>(defaultBranding);
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<SaveButtonState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [hexDraft, setHexDraft] = useState<string>('#2e8a57');

  const [profile, setProfile] = useState<ProfileSettings>({
    displayName: '',
    avatarBase64: null,
    role: 'admin'
  });
  const [credentials, setCredentials] = useState<CredentialSettings | null>(null);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
    submit?: string;
  }>({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [googleDisconnecting, setGoogleDisconnecting] = useState(false);
  const [googleDisconnectConfirm, setGoogleDisconnectConfirm] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [forceBannerDismissed, setForceBannerDismissed] = useState(false);

  const [notifications, setNotifications] = useState<NotificationSettingsState>(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [payroll, setPayroll] = useState<PayrollSettingsState>(DEFAULT_PAYROLL_SETTINGS);
  const [payrollConnectors, setPayrollConnectors] = useState<PayrollConnectorInfo[]>([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollDisconnecting, setPayrollDisconnecting] = useState(false);

  const saveResetTimer = useRef<number | null>(null);

  const clearSaveResetTimer = () => {
    if (saveResetTimer.current) {
      window.clearTimeout(saveResetTimer.current);
      saveResetTimer.current = null;
    }
  };

  const markSaved = () => {
    clearSaveResetTimer();
    setSaveState('saved');
    saveResetTimer.current = window.setTimeout(() => setSaveState('idle'), 2000);
  };

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest<{ branding: Branding | null }>('/api/branding')
      .then((res) => {
        if (cancelled) return;
        const next = { ...defaultBranding, ...(res.branding || {}) };
        setBranding(next);
        setHexDraft(next.brandColor || '#2e8a57');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'security' || tab === 'notifications' || tab === 'team' || tab === 'hoursApproval' || tab === 'payroll') {
      setActiveSection(tab);
    }
    if (params.get('force') === 'true') {
      setForcePasswordChange(true);
      setActiveSection('security');
    }
    if (params.get('linked') === 'true') {
      showToast('Google account connected successfully.');
    }
    const linkMessage = params.get('link_message');
    if (linkMessage) {
      setError(linkMessage);
    } else if (params.get('link_error') === 'already_linked') {
      setError('This Google account is already linked to another user.');
    } else if (params.get('link_error')) {
      setError('Google account could not be connected. Please try again.');
    }
    if (params.get('qb') === 'connected') {
      showToast('Payroll provider connected successfully.');
    } else if (params.get('qb_error')) {
      setError('Payroll provider could not be connected. Please try again.');
    }

    const cleanParams = new URLSearchParams(params);
    cleanParams.delete('linked');
    cleanParams.delete('link_message');
    cleanParams.delete('link_error');
    cleanParams.delete('qb');
    cleanParams.delete('qb_error');
    if (cleanParams.toString() !== params.toString()) {
      const next = cleanParams.toString();
      navigate(next ? `/admin/settings?${next}` : '/admin/settings', { replace: true });
    }
  }, [navigate]);

  const loadSecurityData = useCallback(async () => {
    setSecurityLoading(true);
    setAvatarError(null);
    setGoogleDisconnectConfirm(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setPasswordSuccess(false);
    try {
      const [profileRes, sessionRes] = await Promise.all([
        apiRequest<{
          profile: ProfileSettings;
          credentials: CredentialSettings;
        }>('/api/settings/profile'),
        apiRequest<{ session: SessionInfo }>('/api/settings/session')
      ]);
      setProfile(profileRes.profile);
      setCredentials(profileRes.credentials);
      setSessionInfo(sessionRes.session);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load security settings.');
    } finally {
      setSecurityLoading(false);
    }
  }, []);

  const loadNotificationsData = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const res = await apiRequest<{ settings: NotificationSettingsState }>(
        '/api/settings/notifications'
      );
      setNotifications(res.settings || DEFAULT_NOTIFICATION_SETTINGS);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load notification settings.');
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const loadPayrollData = useCallback(async () => {
    setPayrollLoading(true);
    try {
      const res = await apiRequest<{
        settings: PayrollSettingsState;
        connectors: PayrollConnectorInfo[];
      }>('/api/settings/payroll');
      setPayroll(res.settings || DEFAULT_PAYROLL_SETTINGS);
      setPayrollConnectors(res.connectors || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load payroll settings.');
    } finally {
      setPayrollLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'security') {
      void loadSecurityData();
    }
    if (activeSection === 'notifications') {
      void loadNotificationsData();
    }
    if (activeSection === 'payroll') {
      void loadPayrollData();
    }
  }, [activeSection, loadSecurityData, loadNotificationsData, loadPayrollData]);

  useEffect(() => {
    if (activeSection === 'security' && forcePasswordChange && !forceBannerDismissed) {
      window.setTimeout(() => {
        document.getElementById('settings-password-card')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
    }
  }, [activeSection, forcePasswordChange, forceBannerDismissed, securityLoading]);

  useEffect(() => () => clearSaveResetTimer(), []);

  const logoPreview = useMemo(() => branding.logoBase64 || '/logo.svg', [branding.logoBase64]);

  const update = (key: keyof Branding, value: string) => {
    setBranding((prev) => ({ ...prev, [key]: value }));
  };

  const saveBranding = async () => {
    setSaving(true);
    setSaveState('saving');
    setError(null);
    setSuccess(null);
    try {
      const { pdfVariant: _pv, useBundledAssociationBadges: _bundled, ...payload } = branding;
      const res = await apiRequest<{ branding: Branding }>('/api/branding', {
        method: 'PUT',
        json: payload
      });
      if (res.branding) {
        const next = { ...defaultBranding, ...res.branding };
        setBranding(next);
        setHexDraft(next.brandColor || '#2e8a57');
      }
      setSuccess('Settings saved.');
      await refreshBranding();
      markSaved();
      window.setTimeout(() => setSuccess(null), 3000);
    } catch (e: unknown) {
      setSaveState('idle');
      setError(e instanceof Error ? e.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    const displayName = profile.displayName.trim();
    if (!displayName) {
      setAvatarError(null);
      setError('Display name is required.');
      setSaveState('idle');
      return;
    }

    setSaving(true);
    setSaveState('saving');
    setError(null);
    setAvatarError(null);
    try {
      const res = await apiRequest<{
        profile: ProfileSettings;
        credentials: CredentialSettings;
      }>('/api/settings/profile', {
        method: 'PATCH',
        json: {
          displayName,
          avatarBase64: profile.avatarBase64
        }
      });
      setProfile(res.profile);
      setCredentials(res.credentials);
      await refreshSession();
      markSaved();
    } catch (e: unknown) {
      setSaveState('idle');
      setError(e instanceof Error ? e.message : 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    setSaveState('saving');
    setError(null);
    try {
      const res = await apiRequest<{ settings: NotificationSettingsState }>(
        '/api/settings/notifications',
        {
          method: 'PUT',
          json: notifications
        }
      );
      setNotifications(res.settings);
      markSaved();
    } catch (e: unknown) {
      setSaveState('idle');
      setError(e instanceof Error ? e.message : 'Failed to save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  const savePayroll = async () => {
    setSaving(true);
    setSaveState('saving');
    setError(null);
    try {
      const res = await apiRequest<{
        settings: PayrollSettingsState;
        connectors: PayrollConnectorInfo[];
      }>('/api/settings/payroll', {
        method: 'PUT',
        json: {
          provider: payroll.provider,
          syncOnApprove: payroll.syncOnApprove,
          exportNotifyEmails: payroll.exportNotifyEmails
        }
      });
      setPayroll(res.settings);
      setPayrollConnectors(res.connectors || []);
      markSaved();
    } catch (e: unknown) {
      setSaveState('idle');
      setError(e instanceof Error ? e.message : 'Failed to save payroll settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectPayroll = async () => {
    setPayrollDisconnecting(true);
    setError(null);
    try {
      await apiRequest('/api/payroll/disconnect', { method: 'DELETE' });
      showToast('Payroll provider disconnected.');
      await loadPayrollData();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to disconnect payroll provider.');
    } finally {
      setPayrollDisconnecting(false);
    }
  };

  const save = async () => {
    if (activeSection === 'general' || activeSection === 'pdfManagement') {
      await saveBranding();
      return;
    }
    if (activeSection === 'security') {
      await saveProfile();
      return;
    }
    if (activeSection === 'notifications') {
      await saveNotifications();
      return;
    }
    if (activeSection === 'payroll') {
      await savePayroll();
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setAvatarError(null);
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      setAvatarError('Avatar must be a PNG or JPG image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('Avatar must be 2 MB or smaller.');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setProfile((prev) => ({ ...prev, avatarBase64: dataUrl }));
    } catch (e: unknown) {
      setAvatarError(e instanceof Error ? e.message : 'Failed to read image.');
    }
  };

  const handlePasswordFieldChange = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
    value: string
  ) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordSuccess(false);
    setPasswordErrors((prev) => {
      const next = { ...prev, submit: undefined };
      if (field === 'newPassword') next.newPassword = undefined;
      if (field === 'confirmPassword') next.confirmPassword = undefined;
      return next;
    });
  };

  const handleUpdatePassword = async () => {
    const nextErrors: typeof passwordErrors = {};
    const newPasswordError = validateNewPassword(passwordForm.newPassword);
    if (newPasswordError) nextErrors.newPassword = newPasswordError;
    if (passwordForm.confirmPassword !== passwordForm.newPassword) {
      nextErrors.confirmPassword = 'Confirm password must match new password.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setPasswordErrors(nextErrors);
      return;
    }

    setPasswordSaving(true);
    setPasswordErrors({});
    setPasswordSuccess(false);
    try {
      await apiRequest('/api/auth/change-password', {
        method: 'POST',
        json: passwordForm
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess(true);
      if (forcePasswordChange) {
        setForcePasswordChange(false);
        setForceBannerDismissed(false);
        await refreshSession();
        navigate('/admin/settings?tab=security', { replace: true });
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update password.';
      if (message.toLowerCase().includes('current password')) {
        setPasswordErrors({ submit: 'Current password is incorrect' });
      } else {
        setPasswordErrors({ submit: message });
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const switchSection = (section: SettingsSection) => {
    if (forcePasswordChange) return;
    setActiveSection(section);
    setSaveState('idle');
    setError(null);
    setGoogleDisconnectConfirm(false);
    clearSaveResetTimer();
    if (section === 'hoursApproval' || section === 'team' || section === 'security' || section === 'notifications' || section === 'payroll') {
      navigate(`/admin/settings?tab=${section}`, { replace: true });
    } else {
      navigate('/admin/settings', { replace: true });
    }
  };

  const handleConnectGoogle = () => {
    window.location.assign(`${getApiOrigin()}/api/auth/google/link`);
  };

  const handleConfirmDisconnectGoogle = async () => {
    setGoogleDisconnecting(true);
    setError(null);
    try {
      await apiRequest('/api/auth/google/link', { method: 'DELETE' });
      setGoogleDisconnectConfirm(false);
      showToast('Google account disconnected.');
      await loadSecurityData();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to disconnect Google account.');
    } finally {
      setGoogleDisconnecting(false);
    }
  };

  const templateLabelMap: Record<NonNullable<Branding['pdfTemplate']>, string> = {
    generic: 'Standard',
    premium: 'Premium',
    zera: 'Classic premium'
  };

  /**
   * The "Classic premium" (zera) option is a tenant-licensed layout. We only expose it
   * when the workspace is already using it — every other workspace gets just
   * Standard / Premium.
   */
  const showZeraTemplateOption =
    branding.pdfTemplate === 'zera' || branding.useClientRasterExport === true;

  return (
    <AppShell>
      <div className="quote-page settings-page">
        <div className="quote-container settings-container">
          <header className="quote-header" style={{ marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: '2.1rem', marginBottom: 8 }}>Settings</h1>
              <p style={{ color: 'var(--dash-muted)', margin: 0 }}>
                Workspace profile, PDF defaults, and admin preferences.
              </p>
            </div>
          </header>

          {error && <div className="banner banner-error">{error}</div>}
          {success && <div className="banner banner-success">{success}</div>}

          <div className="settings-layout">
            <aside className="quote-card settings-sidebar">
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'general' ? 'is-active' : ''}`}
                onClick={() => switchSection('general')}
              >
                General
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'pdfManagement' ? 'is-active' : ''}`}
                onClick={() => switchSection('pdfManagement')}
              >
                PDF Management
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'security' ? 'is-active' : ''}`}
                onClick={() => switchSection('security')}
              >
                Security
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'team' ? 'is-active' : ''}`}
                onClick={() => switchSection('team')}
              >
                Team
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'hoursApproval' ? 'is-active' : ''}`}
                onClick={() => switchSection('hoursApproval')}
              >
                Hours approval
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'payroll' ? 'is-active' : ''}`}
                onClick={() => switchSection('payroll')}
              >
                Payroll
              </button>
              <button
                type="button"
                className={`settings-sidebar-item ${activeSection === 'notifications' ? 'is-active' : ''}`}
                onClick={() => switchSection('notifications')}
              >
                Notifications
              </button>
            </aside>

            <div className="settings-content">
              {activeSection === 'general' && (
                <section className="quote-card">
                  <div className="quote-section-title">General</div>
                  <p className="settings-hint">Company details used on documents and across the app.</p>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Company Name</label>
                      <input className="form-input" value={branding.companyName || ''} onChange={(e) => update('companyName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-input" value={branding.phone || ''} onChange={(e) => update('phone', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input className="form-input" value={branding.email || ''} onChange={(e) => update('email', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Website</label>
                      <input className="form-input" value={branding.website || ''} onChange={(e) => update('website', e.target.value)} />
                    </div>
                    <div className="form-group full-width">
                      <label className="form-label">Address</label>
                      <input className="form-input" value={branding.address || ''} onChange={(e) => update('address', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">HST/GST Number</label>
                      <input className="form-input" value={branding.hstNumber || ''} onChange={(e) => update('hstNumber', e.target.value)} />
                    </div>
                  </div>
                </section>
              )}

              {activeSection === 'pdfManagement' && (
                <div className="settings-pdf-management">
                  {/* ─── PDF Studio CTA ─── */}
                  <section className="quote-card settings-pdf-hero">
                    <div className="settings-pdf-hero-content">
                      <span className="settings-pdf-hero-eyebrow">PDF Studio</span>
                      <h2 className="settings-pdf-hero-title">
                        Design every PDF with a live A4 editor
                      </h2>
                      <p className="settings-pdf-hero-sub">
                        Headers, footers, colors, badges, custom HTML, and per-document overrides
                        for quotes, invoices, and inspection reports — all in one full-page editor
                        with a real-time A4 preview.
                      </p>
                      <Link to="/admin/pdf-studio" className="btn-primary settings-pdf-hero-cta">
                        Open PDF Studio →
                      </Link>
                    </div>
                    <ul className="settings-pdf-hero-facts">
                      <li>
                        <span className="settings-pdf-hero-fact-label">Current template</span>
                        <span className="settings-pdf-hero-fact-value">
                          {templateLabelMap[branding.pdfTemplate || 'generic']}
                        </span>
                      </li>
                      <li>
                        <span className="settings-pdf-hero-fact-label">Brand color</span>
                        <span className="settings-pdf-hero-fact-value settings-pdf-hero-fact-color">
                          <span
                            className="settings-pdf-hero-swatch"
                            style={{ background: branding.brandColor || '#2e8a57' }}
                          />
                          {(branding.brandColor || '#2e8a57').toUpperCase()}
                        </span>
                      </li>
                      <li>
                        <span className="settings-pdf-hero-fact-label">Logo</span>
                        <span className="settings-pdf-hero-fact-value">
                          {branding.logoBase64 ? 'Uploaded' : 'Default'}
                        </span>
                      </li>
                    </ul>
                  </section>

                  {/* ─── Defaults: Template ─── */}
                  <section className="quote-card settings-pdf-section-card">
                    <div className="quote-section-title">Template &amp; brand color</div>
                    <p className="settings-hint">
                      These defaults apply across every PDF. Fine-tune individual sections in PDF Studio.
                    </p>

                    <div className="form-group">
                      <label className="form-label" htmlFor="pdf-template-select">
                        PDF layout
                      </label>
                      <select
                        id="pdf-template-select"
                        className="form-input"
                        value={
                          branding.pdfTemplate === 'premium' ||
                          (branding.pdfTemplate === 'zera' && showZeraTemplateOption)
                            ? branding.pdfTemplate
                            : 'generic'
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          const nextTemplate: Branding['pdfTemplate'] =
                            v === 'premium' || (v === 'zera' && showZeraTemplateOption)
                              ? (v as 'premium' | 'zera')
                              : 'generic';
                          setBranding((prev) => ({
                            ...prev,
                            pdfTemplate: nextTemplate,
                            pdfVariant: nextTemplate === 'generic' ? 'generic' : 'premium'
                          }));
                        }}
                      >
                        <option value="generic">Standard — neutral layout</option>
                        <option value="premium">
                          Premium — full layout, association badges only when uploaded
                        </option>
                        {showZeraTemplateOption ? (
                          <option value="zera">
                            Classic premium — fallback association badges when not uploaded
                          </option>
                        ) : null}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="brand-color-hex">
                        Brand color
                      </label>
                      <div className="settings-brand-color-row">
                        <input
                          type="color"
                          aria-label="Pick brand color"
                          className="settings-brand-color-swatch"
                          value={branding.brandColor || '#2e8a57'}
                          onChange={(e) => {
                            const v = e.target.value;
                            update('brandColor', v);
                            setHexDraft(v);
                          }}
                        />
                        <input
                          id="brand-color-hex"
                          className="form-input settings-brand-color-hex"
                          type="text"
                          spellCheck={false}
                          value={hexDraft}
                          onChange={(e) => {
                            const v = e.target.value;
                            setHexDraft(v);
                            if (isValidHex(v)) update('brandColor', v.toLowerCase());
                          }}
                          onBlur={() => {
                            if (!isValidHex(hexDraft)) setHexDraft(branding.brandColor || '#2e8a57');
                          }}
                          placeholder="#2e8a57"
                          maxLength={7}
                        />
                        <span className="settings-brand-color-hint">
                          Used for header accents, buttons, and the dashboard.
                        </span>
                      </div>
                    </div>
                  </section>

                  {/* ─── Defaults: Logo ─── */}
                  <section className="quote-card settings-pdf-section-card">
                    <div className="quote-section-title">Company logo</div>
                    <p className="settings-hint">
                      Shown on PDF headers and the app shell. PNG, JPG, or SVG.
                    </p>

                    <div className="settings-logo-row">
                      <div className="settings-logo-preview-card">
                        <img src={logoPreview} alt="Logo preview" className="settings-logo-preview-img" />
                        <span className="settings-logo-preview-label">
                          {branding.logoBase64 ? 'Current logo' : 'Default placeholder'}
                        </span>
                      </div>
                      <div className="settings-logo-actions">
                        <label className="btn-secondary settings-logo-upload-btn">
                          Upload new logo
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const dataUrl = await fileToDataUrl(file);
                                update('logoBase64', dataUrl);
                              } catch (err: unknown) {
                                setError(err instanceof Error ? err.message : 'Failed to process logo.');
                              }
                            }}
                          />
                        </label>
                        {branding.logoBase64 ? (
                          <button
                            type="button"
                            className="settings-logo-remove-btn"
                            onClick={() => update('logoBase64', '')}
                          >
                            Remove logo
                          </button>
                        ) : null}
                        <p className="settings-hint settings-logo-hint">
                          Recommended: transparent PNG or SVG, at least 240×80 px.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* ─── Defaults: Footer ─── */}
                  <section className="quote-card settings-pdf-section-card">
                    <div className="quote-section-title">Footer text</div>
                    <p className="settings-hint">
                      Disclaimers and compliance text added to the bottom of every PDF. Leave blank
                      to use sensible defaults.
                    </p>

                    <div className="form-group">
                      <label className="form-label" htmlFor="footer-disclaimer">
                        Disclaimer
                      </label>
                      <textarea
                        id="footer-disclaimer"
                        className="form-textarea"
                        rows={3}
                        value={branding.disclaimerText || ''}
                        onChange={(e) => update('disclaimerText', e.target.value)}
                        placeholder="e.g. This quote is valid for 30 days from the date of issue."
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="footer-compliance">
                        Compliance / footer note
                      </label>
                      <textarea
                        id="footer-compliance"
                        className="form-textarea"
                        rows={3}
                        value={branding.complianceText || ''}
                        onChange={(e) => update('complianceText', e.target.value)}
                        placeholder="e.g. CFAA Registered. ULC-S536 inspection in accordance with NFC."
                      />
                    </div>
                  </section>
                </div>
              )}

              {activeSection === 'security' && (
                <>
                <AdminFieldModeToggle />
                <SecuritySettingsTab
                  profile={profile}
                  credentials={credentials}
                  session={sessionInfo}
                  profileLoading={securityLoading}
                  passwordForm={passwordForm}
                  passwordErrors={passwordErrors}
                  passwordSaving={passwordSaving}
                  passwordSuccess={passwordSuccess}
                  googleDisconnecting={googleDisconnecting}
                  googleDisconnectConfirm={googleDisconnectConfirm}
                  forcePasswordChange={forcePasswordChange && !forceBannerDismissed}
                  onDismissForceBanner={() => setForceBannerDismissed(true)}
                  onProfileChange={(patch) => setProfile((prev) => ({ ...prev, ...patch }))}
                  onAvatarUpload={handleAvatarUpload}
                  onRemoveAvatar={() => setProfile((prev) => ({ ...prev, avatarBase64: null }))}
                  avatarError={avatarError}
                  onPasswordFieldChange={handlePasswordFieldChange}
                  onUpdatePassword={handleUpdatePassword}
                  onSignOutOthers={() => showToast('No other active sessions found.')}
                  onConnectGoogle={handleConnectGoogle}
                  onRequestDisconnectGoogle={() => setGoogleDisconnectConfirm(true)}
                  onCancelDisconnectGoogle={() => setGoogleDisconnectConfirm(false)}
                  onConfirmDisconnectGoogle={handleConfirmDisconnectGoogle}
                />
                </>
              )}

              {activeSection === 'team' && <TeamSettingsTab />}

              {activeSection === 'hoursApproval' && <HoursApprovalSection />}

              {activeSection === 'payroll' && (
                <PayrollSettingsSection
                  settings={payroll}
                  connectors={payrollConnectors}
                  loading={payrollLoading}
                  saving={saving}
                  disconnecting={payrollDisconnecting}
                  onProviderChange={(provider) => setPayroll((prev) => ({ ...prev, provider }))}
                  onSyncOnApproveChange={(syncOnApprove) => setPayroll((prev) => ({ ...prev, syncOnApprove }))}
                  onNotifyEmailsChange={(raw) =>
                    setPayroll((prev) => ({
                      ...prev,
                      exportNotifyEmails: raw
                        .split(',')
                        .map((e) => e.trim().toLowerCase())
                        .filter(Boolean)
                    }))
                  }
                  onDisconnect={() => void handleDisconnectPayroll()}
                  onSettingsRefresh={(payload) => {
                    setPayroll(payload.settings);
                    setPayrollConnectors(payload.connectors);
                    showToast('Request saved — we use this to prioritize new connectors.');
                  }}
                />
              )}

              {activeSection === 'notifications' && (
                <NotificationsSettingsTab
                  settings={notifications}
                  loading={notificationsLoading}
                  onEmailAlertChange={(key, value) =>
                    setNotifications((prev) => ({
                      ...prev,
                      emailAlerts: { ...prev.emailAlerts, [key]: value }
                    }))
                  }
                  onReminderChange={(patch) =>
                    setNotifications((prev) => ({
                      ...prev,
                      inspectionReminder: { ...prev.inspectionReminder, ...patch }
                    }))
                  }
                />
              )}
            </div>
          </div>

          {activeSection !== 'team' && activeSection !== 'hoursApproval' ? (
          <div className="quote-actions settings-save-row">
            <button
              type="button"
              className={`btn-primary settings-save-btn${saveState === 'saved' ? ' is-saved' : ''}`}
              onClick={save}
              disabled={
                loading ||
                saving ||
                saveState === 'saving' ||
                forcePasswordChange ||
                (activeSection === 'security' && securityLoading) ||
                (activeSection === 'notifications' && notificationsLoading) ||
                (activeSection === 'payroll' && payrollLoading)
              }
            >
              {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved ✓' : 'Save settings'}
            </button>
          </div>
          ) : null}
        </div>
      </div>
      {toast ? <div className="settings-toast">{toast}</div> : null}
    </AppShell>
  );
}
